import * as yup from 'yup'
import { LOCATION_SEARCH_TYPE } from '../../../types'
import { FLY } from '../constants'

export const LOCATION_REQUIRED =
  'Please enter your City, Airport, Point of Interest, Hotel Name or U.S. Zip Code'
export const INVALID_DATE = 'Please select valid dates'
const DISALLOW_INFANT_INTL_FLIGHT =
  'Sorry, Priceline does not offer lap infant seating on international flights'
const INFANT_CAPACITY_LIMIT = 'Only one infant per adult is allowed'
export const CHILD_AGE_REQUIRED =
  "Please enter children's age to get the best price for your group"
const TRAVELERS_PER_ROOM = 4
export const ROOM_CAPACITY_ERROR =
  'Rooms accommodate up to 4 people. You may need additional rooms for your group'

const intlLocation = (locationObject: LOCATION_SEARCH_TYPE | null) =>
  locationObject &&
  typeof locationObject.countryCode === 'string' &&
  locationObject.countryCode.toUpperCase() !== 'US'

export const isInfantFound = (childrenAges: number[]) =>
  Array.isArray(childrenAges) && childrenAges.some(age => Number(age) === 0)

export const isLocationWithInfant = (
  locationObject: LOCATION_SEARCH_TYPE | null,
  childrenAges: number[]
) => {
  return intlLocation(locationObject) && isInfantFound(childrenAges)
}

const isChildrenAgeDataValid = (childrenAges: number[]) =>
  Array.isArray(childrenAges)
export const numOfValidAges = (childrenAges: number[]): number[] => {
  if (Array.isArray(childrenAges)) {
    return childrenAges.filter(age => age >= 0)
  }
  return []
}

const isInfantOnInternationalTravel = (
  location: LOCATION_SEARCH_TYPE | null,
  childrenAges: number[]
) => {
  return isLocationWithInfant(location, childrenAges)
}

function isChildrenAgeNotProvided(
  childrenCount: number,
  childrenAges: number[]
) {
  return (
    childrenCount > 0 &&
    isChildrenAgeDataValid(childrenAges) &&
    childrenCount !== numOfValidAges(childrenAges).length
  )
}

const isInfantLimitReachedPackages = (
  adultCount: number,
  childrenAges: number[]
) => {
  const lapInfants = childrenAges.filter(age => age === 0)
  const infantCount = lapInfants.length
  return infantCount > adultCount
}

function handleInfantChildrenTravel(this: yup.TestContext) {
  const {
    adultCount,
    childrenAges,
    startLocation,
    endLocation,
    childrenCount,
    tripType
  } = this.parent

  if (
    tripType.includes(FLY) &&
    (isInfantOnInternationalTravel(endLocation, childrenAges) ||
      isInfantOnInternationalTravel(startLocation, childrenAges))
  ) {
    return this.createError({
      message: DISALLOW_INFANT_INTL_FLIGHT,
      path: this.path
    })
  }

  if (isInfantLimitReachedPackages(adultCount, childrenAges)) {
    return this.createError({
      message: INFANT_CAPACITY_LIMIT,
      path: this.path
    })
  }

  if (isChildrenAgeNotProvided(childrenCount, childrenAges)) {
    return this.createError({
      message: CHILD_AGE_REQUIRED,
      path: this.path
    })
  }

  return true
}

function handleRoomCapacity(this: yup.TestContext) {
  const { adultCount, roomCount, childrenCount, tripType } = this.parent

  const travelerCount = adultCount + childrenCount
  const allowedTravelerCount = roomCount * TRAVELERS_PER_ROOM

  if (allowedTravelerCount < travelerCount && tripType.includes(FLY)) {
    return this.createError({
      message: ROOM_CAPACITY_ERROR,
      path: this.path
    })
  }

  return true
}

export const searchFormSchema = yup.object().shape({
  tripType: yup.string(),
  partialStay: yup.bool(),
  startLocation: yup
    .object()
    .nullable()
    .when('tripType', {
      is: (tripType: string) => tripType.includes(FLY),
      then: yup
        .object()
        .nullable()
        .required(LOCATION_REQUIRED),
      otherwise: yup.object().nullable()
    }),
  endLocation: yup
    .object()
    .nullable()
    .required(LOCATION_REQUIRED),
  startDate: yup.string().required(INVALID_DATE),
  endDate: yup.string().required(INVALID_DATE),
  childrenCount: yup.number().test({
    name: 'childrenCount',
    test: handleInfantChildrenTravel
  }),
  roomCount: yup.number().test({
    name: 'roomCount',
    test: handleRoomCapacity
  })
})
